import { useEffect, useRef, useState } from 'react'

export interface UseShortLoadOptions {
  delayMs?: number
  minVisibleMs?: number
}

/**
 * Delays showing a loader until `loading` has stayed true for `delayMs` (default 300ms).
 * Once visible, keeps the loader visible for at least `minVisibleMs` (default 400ms)
 * after it became visible and until `loading` becomes false.
 */
export const useShortLoad = (
  loading: boolean,
  opts?: UseShortLoadOptions,
): boolean => {
  const delayMs = opts?.delayMs ?? 300
  const minVisibleMs = opts?.minVisibleMs ?? 400

  const [visible, setVisible] = useState(false)
  const becameVisibleAtRef = useRef<number | null>(null)
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const minVisibleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (loading) {
      if (minVisibleTimerRef.current) {
        clearTimeout(minVisibleTimerRef.current)
        minVisibleTimerRef.current = null
      }
      if (!visible && !delayTimerRef.current) {
        delayTimerRef.current = setTimeout(() => {
          becameVisibleAtRef.current = Date.now()
          setVisible(true)
          delayTimerRef.current = null
        }, delayMs)
      }
    } else {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current)
        delayTimerRef.current = null
      }
      if (visible) {
        const elapsed = becameVisibleAtRef.current !== null
          ? Date.now() - becameVisibleAtRef.current
          : minVisibleMs
        const remaining = minVisibleMs - elapsed
        if (remaining <= 0) {
          setVisible(false)
          becameVisibleAtRef.current = null
        } else if (!minVisibleTimerRef.current) {
          minVisibleTimerRef.current = setTimeout(() => {
            setVisible(false)
            becameVisibleAtRef.current = null
            minVisibleTimerRef.current = null
          }, remaining)
        }
      }
    }
  }, [loading, visible, delayMs, minVisibleMs])

  useEffect(() => {
    return () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current)
        delayTimerRef.current = null
      }
      if (minVisibleTimerRef.current) {
        clearTimeout(minVisibleTimerRef.current)
        minVisibleTimerRef.current = null
      }
    }
  }, [])

  return visible
}
